import React from 'react';

import type { ClickedStop } from '../overlays/createStopOverlays';
import { PICKUP_COLOR, DELIVERY_COLOR } from '../constants';
import { GoogleMapsElement, useMapSurfaceColors } from '@components/Maps';

import { css } from '@emotion/css';

// COMPONENT ----------------------------------------------------------------- //

interface StopInfoPopupProps {
  stop: ClickedStop;
  onClose: () => void;
}

const StopInfoPopup = ({ stop, onClose }: StopInfoPopupProps) => {
  const surface = useMapSurfaceColors();
  const cls = useStyles(surface.divider);
  const { type, location } = stop;
  const color = type === 'pickup' ? PICKUP_COLOR : DELIVERY_COLOR;

  // Position near the click point, with viewport boundary detection
  const popupRef = React.useRef<HTMLDivElement>(null);
  const [style, setStyle] = React.useState<React.CSSProperties>({
    left: stop.screenX + 14,
    top: stop.screenY - 12,
  });

  React.useEffect(() => {
    const el = popupRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = stop.screenX + 14;
    let top = stop.screenY - 12;
    if (rect.right > window.innerWidth - 8) {
      left = stop.screenX - rect.width - 14;
    }
    if (rect.bottom > window.innerHeight - 8) {
      top = stop.screenY - rect.height + 12;
    }
    if (top < 8) top = 8;
    setStyle({ left, top });
  }, [stop.screenX, stop.screenY]);

  // Close on Escape
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close on click outside
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay to avoid the originating click
    const timer = setTimeout(() => window.addEventListener('mousedown', handleClick), 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const geofenceLabel = React.useMemo(() => {
    if (location.geofence_vertices && location.geofence_vertices.length >= 3) {
      return `Custom polygon (${location.geofence_vertices.length} points)`;
    }
    const radiusM = location.geofence_radius;
    if (radiusM != null && radiusM > 0) {
      const radiusFt = Math.round(radiusM * 3.28084);
      return `${radiusM}m (${radiusFt}ft) radius`;
    }
    return 'Default (200m)';
  }, [location]);

  return (
    <GoogleMapsElement
      ref={popupRef}
      style={{
        ...style,
        zIndex: 25,
        padding: '10px 14px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        fontFamily: "'Inter Tight', sans-serif",
        fontSize: 12,
        lineHeight: 1.5,
        minWidth: 180,
        maxWidth: 280,
        pointerEvents: 'auto',
      }}
    >
      {/* Header */}
      <div className={cls.header}>
        <span className={cls.typeDot} style={{ backgroundColor: color }} />
        <span className={cls.typeLabel} style={{ color }}>
          {type === 'pickup' ? 'Pickup' : 'Delivery'}
        </span>
      </div>

      <div className={cls.divider} />

      {/* Name */}
      {location.name ? (
        <div className={cls.row}>
          <span className={cls.label} style={{ color: surface.disabled }}>Name</span>
          <span className={cls.value} style={{ color: surface.primary }}>{location.name}</span>
        </div>
      ) : null}

      {/* Address */}
      {location.address ? (
        <div className={cls.row}>
          <span className={cls.label} style={{ color: surface.disabled }}>Address</span>
          <span className={cls.valueWrap} style={{ color: surface.primary }}>{location.address}</span>
        </div>
      ) : null}

      {/* Geofence */}
      <div className={cls.row}>
        <span className={cls.label} style={{ color: surface.disabled }}>Geofence</span>
        <span className={cls.value} style={{ color: surface.primary }}>{geofenceLabel}</span>
      </div>

      {/* Consumer info (concierge/valet moves) */}
      {stop.consumerName ? (
        <>
          <div className={cls.divider} />
          <div className={cls.row}>
            <span className={cls.label} style={{ color: surface.disabled }}>Customer</span>
            <span className={cls.value} style={{ color: surface.primary }}>{stop.consumerName}</span>
          </div>
          {stop.consumerPhone ? (
            <div className={cls.row}>
              <span className={cls.label} style={{ color: surface.disabled }}>Phone</span>
              <span className={cls.value} style={{ color: surface.primary }}>{stop.consumerPhone}</span>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Coordinates */}
      {location.latitude != null && location.longitude != null ? (
        <div className={cls.row}>
          <span className={cls.label} style={{ color: surface.disabled }}>Coords</span>
          <span className={cls.valueMuted} style={{ color: surface.secondary }}>
            {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
          </span>
        </div>
      ) : null}
    </GoogleMapsElement>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (dividerColor?: string) => ({
  header: css`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
  `,
  typeDot: css`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  `,
  typeLabel: css`
    font-weight: 700;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  `,
  divider: css`
    height: 1px;
    background-color: ${dividerColor || 'rgba(255, 255, 255, 0.1)'};
    margin: 4px 0;
  `,
  row: css`
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 1px 0;
  `,
  label: css`
    font-size: 11px;
    min-width: 52px;
    flex-shrink: 0;
  `,
  value: css`
    font-weight: 600;
    font-size: 12px;
  `,
  valueWrap: css`
    font-weight: 600;
    font-size: 12px;
    white-space: normal;
    word-break: break-word;
  `,
  valueMuted: css`
    font-size: 11px;
    font-family: monospace;
  `,
});

export default StopInfoPopup;
